/**
 * Crypto Quiz Page - Test Your Knowledge
 *
 * Gamified quiz experience to test and improve crypto knowledge
 * with explanations linking to blog articles.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useToast } from '../components/Toast';
import { SEO, getPageSEO } from '../components/SEO';
import {
  QUIZ_QUESTIONS,
  getRandomQuestion,
  CATEGORY_INFO
} from '../data/quizQuestions';
import { recordQuizResponse } from '../services/quizAnalyticsService';
import type { QuizQuestion, QuizProgress, QuizCategory, SupportedLanguage } from '../types/quiz';
import { getLocalizedText, getLocalizedArray } from '../types/quiz';
import './CryptoQuiz.css';

// Animated Quiz Mascot - Wise Owl
const QuizMascot = ({ message }: { message: string }) => {
  return (
    <div className="quiz-mascot">
      <div className="mascot-bubble">{message}</div>
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="owlGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a78bfa" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
          <linearGradient id="eyeGlow" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>

        {/* Wings */}
        <ellipse className="mascot-wing-left" cx="22" cy="55" rx="12" ry="20" fill="#7c3aed" opacity="0.8" />
        <ellipse className="mascot-wing-right" cx="78" cy="55" rx="12" ry="20" fill="#7c3aed" opacity="0.8" />

        {/* Body */}
        <ellipse className="mascot-body" cx="50" cy="55" rx="32" ry="35" />

        {/* Belly */}
        <ellipse cx="50" cy="62" rx="20" ry="22" fill="#c4b5fd" opacity="0.5" />

        {/* Face circle */}
        <circle cx="50" cy="40" r="28" fill="#ede9fe" />

        {/* Eye backgrounds */}
        <ellipse cx="38" cy="38" rx="12" ry="14" fill="url(#eyeGlow)" />
        <ellipse cx="62" cy="38" rx="12" ry="14" fill="url(#eyeGlow)" />

        {/* Eyes */}
        <ellipse className="mascot-eye-left" cx="38" cy="38" rx="8" ry="10" />
        <ellipse className="mascot-eye-right" cx="62" cy="38" rx="8" ry="10" />

        {/* Pupils */}
        <circle className="mascot-pupil" cx="40" cy="38" r="3" />
        <circle className="mascot-pupil" cx="64" cy="38" r="3" />

        {/* Beak */}
        <path d="M 45 48 L 50 58 L 55 48 Z" fill="#f59e0b" />

        {/* Eyebrows / Tufts */}
        <path d="M 25 22 Q 35 18 38 25" stroke="#7c3aed" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M 75 22 Q 65 18 62 25" stroke="#7c3aed" strokeWidth="3" fill="none" strokeLinecap="round" />

        {/* Ear tufts */}
        <path d="M 28 15 L 32 25 L 25 22 Z" fill="#8b5cf6" />
        <path d="M 72 15 L 68 25 L 75 22 Z" fill="#8b5cf6" />

        {/* Sparkles (appear on hover) */}
        <g className="mascot-sparkle" style={{ transformOrigin: '15px 20px' }}>
          <path d="M 15 15 L 15 25 M 10 20 L 20 20" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
        </g>
        <g className="mascot-sparkle" style={{ transformOrigin: '85px 25px' }}>
          <path d="M 85 20 L 85 30 M 80 25 L 90 25" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
        </g>

        {/* Feet */}
        <ellipse cx="40" cy="88" rx="8" ry="4" fill="#f59e0b" />
        <ellipse cx="60" cy="88" rx="8" ry="4" fill="#f59e0b" />
      </svg>
    </div>
  );
};

// LocalStorage key
const STORAGE_KEY = 'wealthhub_quiz_progress';

// XP per correct answer based on difficulty
const XP_REWARDS = {
  easy: 10,
  medium: 20,
  hard: 35
};

// Initial progress state
const initialProgress: QuizProgress = {
  totalAnswered: 0,
  correctAnswers: 0,
  currentStreak: 0,
  bestStreak: 0,
  answeredQuestions: [],
  categoryStats: {
    'blockchain-basics': { correct: 0, total: 0 },
    'defi': { correct: 0, total: 0 },
    'staking': { correct: 0, total: 0 },
    'trading': { correct: 0, total: 0 },
    'security': { correct: 0, total: 0 },
    'flux': { correct: 0, total: 0 },
    'hedera': { correct: 0, total: 0 },
    'bitcoin': { correct: 0, total: 0 },
    'ethereum': { correct: 0, total: 0 },
    'general': { correct: 0, total: 0 }
  }
};

// Calculate XP from progress
const calculateXP = (progress: QuizProgress): number => {
  let xp = 0;
  xp = progress.correctAnswers * 15;
  xp += progress.bestStreak * 5;
  return xp;
};

// Level thresholds
const LEVEL_THRESHOLDS = [
  { level: 1, xp: 0 },
  { level: 2, xp: 50 },
  { level: 3, xp: 150 },
  { level: 4, xp: 300 },
  { level: 5, xp: 500 },
  { level: 6, xp: 750 },
  { level: 7, xp: 1000 },
  { level: 8, xp: 1500 },
];

// Get current level
const getCurrentLevel = (xp: number) => {
  let currentLevel = LEVEL_THRESHOLDS[0];
  for (const level of LEVEL_THRESHOLDS) {
    if (xp >= level.xp) {
      currentLevel = level;
    } else {
      break;
    }
  }
  const nextLevel = LEVEL_THRESHOLDS.find(l => l.xp > xp) || currentLevel;
  const progressToNext = nextLevel.xp > currentLevel.xp
    ? ((xp - currentLevel.xp) / (nextLevel.xp - currentLevel.xp)) * 100
    : 100;

  return { ...currentLevel, nextLevel, progressToNext, currentXP: xp };
};

// Mascot messages (will be translated)
const MASCOT_MESSAGES_KEYS = [
  'Test yourself!',
  'You can do it!',
  'Let\'s quiz!',
  'Show what you know!'
];

export function CryptoQuiz() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language === 'en' ? 'en' : 'fr') as SupportedLanguage;
  const toast = useToast();

  // Mascot message (random on mount)
  const [mascotMessage] = useState(() => {
    const messages = i18n.language === 'fr'
      ? ['Teste-toi !', 'Tu peux le faire !', 'Allez, un quiz !', 'Montre ce que tu sais !']
      : MASCOT_MESSAGES_KEYS;
    return messages[Math.floor(Math.random() * messages.length)];
  });

  // State
  const [progress, setProgress] = useState<QuizProgress>(initialProgress);
  const [currentQuestion, setCurrentQuestion] = useState<QuizQuestion | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<QuizCategory | 'all'>('all');
  const [isLoading, setIsLoading] = useState(true);

  // Timer ref for tracking time spent on questions
  const questionStartTime = useRef<number>(Date.now());

  // Calculate XP and level
  const xp = useMemo(() => calculateXP(progress), [progress]);
  const levelInfo = useMemo(() => getCurrentLevel(xp), [xp]);

  // Get level title
  const getLevelTitle = (level: number) => {
    return t(`quiz.levels.${level}`, { defaultValue: `Level ${level}` });
  };

  // Load progress from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setProgress(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading quiz progress:', error);
    }
    setIsLoading(false);
  }, []);

  // Save progress to localStorage
  const saveProgress = useCallback((newProgress: QuizProgress) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newProgress));
      setProgress(newProgress);
    } catch (error) {
      console.error('Error saving quiz progress:', error);
    }
  }, []);

  // Get next question
  const getNextQuestion = useCallback(() => {
    let question: QuizQuestion | null = null;

    if (selectedCategory === 'all') {
      question = getRandomQuestion(progress.answeredQuestions);
    } else {
      const categoryQuestions = QUIZ_QUESTIONS.filter(
        q => q.category === selectedCategory && !progress.answeredQuestions.includes(q.id)
      );
      if (categoryQuestions.length > 0) {
        question = categoryQuestions[Math.floor(Math.random() * categoryQuestions.length)];
      }
    }

    setCurrentQuestion(question);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setIsCorrect(null);

    // Reset timer for new question
    questionStartTime.current = Date.now();
  }, [selectedCategory, progress.answeredQuestions]);

  // Load first question
  useEffect(() => {
    if (!isLoading && !currentQuestion) {
      getNextQuestion();
    }
  }, [isLoading, currentQuestion, getNextQuestion]);

  // Handle answer selection
  const handleAnswerSelect = (index: number) => {
    if (isAnswered) return;
    setSelectedAnswer(index);
  };

  // Handle answer submission
  const handleSubmit = () => {
    if (selectedAnswer === null || !currentQuestion) return;

    const correct = selectedAnswer === currentQuestion.correctAnswer;
    setIsCorrect(correct);
    setIsAnswered(true);

    // Calculate time spent on question
    const timeSpentMs = Date.now() - questionStartTime.current;

    // Record response to database (async, non-blocking)
    recordQuizResponse({
      questionId: currentQuestion.id,
      category: currentQuestion.category,
      difficulty: currentQuestion.difficulty,
      selectedAnswer,
      correctAnswer: currentQuestion.correctAnswer,
      isCorrect: correct,
      timeSpentMs,
    }).catch(err => {
      // Silently log errors - don't disrupt user experience
      console.warn('Failed to record quiz response:', err);
    });

    // Update progress
    const newProgress: QuizProgress = {
      ...progress,
      totalAnswered: progress.totalAnswered + 1,
      correctAnswers: correct ? progress.correctAnswers + 1 : progress.correctAnswers,
      currentStreak: correct ? progress.currentStreak + 1 : 0,
      bestStreak: correct
        ? Math.max(progress.bestStreak, progress.currentStreak + 1)
        : progress.bestStreak,
      answeredQuestions: [...progress.answeredQuestions, currentQuestion.id],
      categoryStats: {
        ...progress.categoryStats,
        [currentQuestion.category]: {
          correct: progress.categoryStats[currentQuestion.category].correct + (correct ? 1 : 0),
          total: progress.categoryStats[currentQuestion.category].total + 1
        }
      },
      lastPlayedAt: new Date().toISOString()
    };

    saveProgress(newProgress);

    if (correct) {
      const earnedXP = XP_REWARDS[currentQuestion.difficulty];
      toast(`+${earnedXP} XP ! ${t('quiz.excellent')}`, 'success');
    }
  };

  // Handle next question
  const handleNext = () => {
    getNextQuestion();
  };

  // Reset progress
  const handleReset = () => {
    if (confirm(t('quiz.resetConfirm'))) {
      saveProgress(initialProgress);
      setCurrentQuestion(null);
      toast(t('quiz.resetDone'), 'info');
      setTimeout(() => getNextQuestion(), 100);
    }
  };

  // Calculate accuracy
  const accuracy = progress.totalAnswered > 0
    ? Math.round((progress.correctAnswers / progress.totalAnswered) * 100)
    : 0;

  // Get difficulty label
  const getDifficultyLabel = (difficulty: string) => {
    return t(`quiz.difficulty.${difficulty}`, { defaultValue: difficulty });
  };

  // Available categories with counts
  const categoriesWithCounts = useMemo(() => {
    const counts: Record<string, { total: number; answered: number }> = {};
    QUIZ_QUESTIONS.forEach(q => {
      if (!counts[q.category]) {
        counts[q.category] = { total: 0, answered: 0 };
      }
      counts[q.category].total++;
      if (progress.answeredQuestions.includes(q.id)) {
        counts[q.category].answered++;
      }
    });
    return Object.entries(CATEGORY_INFO)
      .filter(([key]) => counts[key]?.total > 0)
      .map(([key, info]) => ({
        key,
        ...info,
        total: counts[key]?.total || 0,
        answered: counts[key]?.answered || 0
      }));
  }, [progress.answeredQuestions]);

  if (isLoading) {
    return (
      <div className="quiz-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Icons.Loader size={32} className="spin" />
      </div>
    );
  }

  // Get SEO config for quiz page
  const seoConfig = getPageSEO('quiz');

  return (
    <div className="quiz-page">
      <SEO {...seoConfig} />
      {/* Hero Section */}
      <div className="quiz-hero">
        {/* Animated Mascot */}
        <QuizMascot message={mascotMessage} />

        <div className="quiz-hero__content">
          <div className="quiz-hero__left">
            <div className="quiz-hero__badge">
              <Icons.Brain size={14} />
              {t('quiz.badge')}
            </div>
            <h1 className="quiz-hero__title">{t('quiz.title')}</h1>
            <p className="quiz-hero__subtitle">
              {t('quiz.subtitle')}
            </p>

            {/* Level Progress */}
            <div className="quiz-level">
              <div className="quiz-level__header">
                <div className="quiz-level__title">
                  <Icons.Award size={16} />
                  {t('quiz.level', { level: levelInfo.level, title: getLevelTitle(levelInfo.level) }).replace('{{level}}', String(levelInfo.level)).replace('{{title}}', getLevelTitle(levelInfo.level))}
                </div>
                <span className="quiz-level__xp">
                  {xp} / {levelInfo.nextLevel.xp} XP
                </span>
              </div>
              <div className="quiz-level__bar">
                <div
                  className="quiz-level__progress"
                  style={{ width: `${levelInfo.progressToNext}%` }}
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="quiz-hero__stats">
            <div className="quiz-stat-card">
              <div className="quiz-stat-card__value quiz-stat-card__value--purple">
                {progress.totalAnswered}
              </div>
              <div className="quiz-stat-card__label">{t('quiz.stats.questions')}</div>
            </div>
            <div className="quiz-stat-card">
              <div className="quiz-stat-card__value quiz-stat-card__value--green">
                {accuracy}%
              </div>
              <div className="quiz-stat-card__label">{t('quiz.stats.accuracy')}</div>
            </div>
            <div className="quiz-stat-card">
              <div className="quiz-stat-card__value quiz-stat-card__value--orange">
                {progress.currentStreak}
              </div>
              <div className="quiz-stat-card__label">{t('quiz.stats.streak')}</div>
            </div>
            <div className="quiz-stat-card">
              <div className="quiz-stat-card__value quiz-stat-card__value--cyan">
                {xp}
              </div>
              <div className="quiz-stat-card__label">{t('quiz.stats.totalXp')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Selection */}
      <div className="quiz-categories">
        <div className="quiz-categories__title">{t('quiz.filterByCategory')}</div>
        <div className="quiz-categories__grid">
          <button
            onClick={() => {
              setSelectedCategory('all');
              setCurrentQuestion(null);
            }}
            className={`quiz-category-chip ${selectedCategory === 'all' ? 'quiz-category-chip--active' : ''}`}
          >
            <span className="quiz-category-chip__dot" style={{ background: '#8b5cf6' }} />
            {t('quiz.all')}
            <span className="quiz-category-chip__count">
              {QUIZ_QUESTIONS.length - progress.answeredQuestions.length}
            </span>
          </button>
          {categoriesWithCounts.map((cat) => (
            <button
              key={cat.key}
              onClick={() => {
                setSelectedCategory(cat.key as QuizCategory);
                setCurrentQuestion(null);
              }}
              className={`quiz-category-chip ${selectedCategory === cat.key ? 'quiz-category-chip--active' : ''}`}
            >
              <span className="quiz-category-chip__dot" style={{ background: cat.color }} />
              {cat.label}
              <span className="quiz-category-chip__count">
                {cat.total - cat.answered}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="quiz-main">
        {/* Question Card */}
        {currentQuestion ? (
          <div className="quiz-card">
            {/* Question Header */}
            <div className="quiz-question__header">
              <div className="quiz-question__meta">
                <span
                  className="quiz-question__category"
                  style={{ background: CATEGORY_INFO[currentQuestion.category]?.color || '#6b7280' }}
                >
                  {CATEGORY_INFO[currentQuestion.category]?.label || currentQuestion.category}
                </span>
                <span className={`quiz-question__difficulty quiz-question__difficulty--${currentQuestion.difficulty}`}>
                  {getDifficultyLabel(currentQuestion.difficulty)}
                </span>
              </div>
              <span className="quiz-question__number">
                {t('quiz.questionNumber', { number: progress.totalAnswered + 1 }).replace('{{number}}', String(progress.totalAnswered + 1))}
              </span>
            </div>

            {/* Question Content */}
            <div className="quiz-question__content">
              <h2 className="quiz-question__text">
                {getLocalizedText(currentQuestion.question, lang)}
              </h2>

              {/* Options */}
              <div className="quiz-options">
                {getLocalizedArray(currentQuestion.options, lang).map((option, index) => {
                  const isSelected = selectedAnswer === index;
                  const isCorrectOption = index === currentQuestion.correctAnswer;
                  const showResult = isAnswered;

                  let optionClass = 'quiz-option';
                  if (showResult) {
                    if (isCorrectOption) {
                      optionClass += ' quiz-option--correct';
                    } else if (isSelected && !isCorrect) {
                      optionClass += ' quiz-option--incorrect';
                    }
                    if (!isSelected && !isCorrectOption) {
                      optionClass += ' quiz-option--disabled';
                    }
                  } else if (isSelected) {
                    optionClass += ' quiz-option--selected';
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={isAnswered}
                      className={optionClass}
                    >
                      <div className="quiz-option__letter">
                        {showResult && isCorrectOption ? (
                          <Icons.Check size={18} />
                        ) : showResult && isSelected && !isCorrect ? (
                          <Icons.X size={18} />
                        ) : (
                          String.fromCharCode(65 + index)
                        )}
                      </div>
                      <span className="quiz-option__text">{option}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Action Button - Always at the same position */}
            <div className="quiz-action">
              {!isAnswered ? (
                <button
                  onClick={handleSubmit}
                  disabled={selectedAnswer === null}
                  className="quiz-action__btn quiz-action__btn--primary"
                >
                  {t('quiz.validateAnswer')}
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="quiz-action__btn quiz-action__btn--success"
                >
                  {t('quiz.nextQuestion')}
                  <Icons.ArrowRight size={18} />
                </button>
              )}
            </div>

            {/* Explanation - Appears below the button after answering */}
            {isAnswered && (
              <div className={`quiz-explanation ${isCorrect ? 'quiz-explanation--correct' : 'quiz-explanation--incorrect'}`}>
                <div className={`quiz-explanation__header ${isCorrect ? 'quiz-explanation__header--correct' : 'quiz-explanation__header--incorrect'}`}>
                  {isCorrect ? (
                    <>
                      <Icons.CheckCircle size={20} />
                      {t('quiz.excellent')} +{XP_REWARDS[currentQuestion.difficulty]} XP
                    </>
                  ) : (
                    <>
                      <Icons.X size={20} />
                      {t('quiz.notQuite')}
                    </>
                  )}
                </div>

                <div className="quiz-explanation__content">
                  <Icons.Lightbulb size={20} className="quiz-explanation__icon" />
                  <div>
                    <p className="quiz-explanation__text">
                      {getLocalizedText(currentQuestion.explanation, lang)}
                    </p>

                    {/* Article link - temporarily disabled */}
                    {currentQuestion.articleUrl && currentQuestion.articleTitle && (
                      <span className="quiz-explanation__link quiz-explanation__link--disabled">
                        <Icons.BookOpen size={14} />
                        {getLocalizedText(currentQuestion.articleTitle, lang)}
                        <span className="quiz-explanation__link-soon">
                          ({i18n.language === 'fr' ? 'bientôt' : 'coming soon'})
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* No more questions */
          <div className="quiz-card">
            <div className="quiz-completed">
              <div className="quiz-completed__icon">
                <Icons.Award size={36} />
              </div>
              <h2 className="quiz-completed__title">{t('quiz.congratulations')}</h2>
              <p className="quiz-completed__text">
                {selectedCategory === 'all'
                  ? t('quiz.completedAll')
                  : t('quiz.completedCategory')}
              </p>
              <div className="quiz-completed__actions">
                <button
                  onClick={handleReset}
                  className="btn btn-outline"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Icons.RefreshCw size={16} />
                  {t('quiz.restart')}
                </button>
                {selectedCategory !== 'all' && (
                  <button
                    onClick={() => {
                      setSelectedCategory('all');
                      setTimeout(() => getNextQuestion(), 100);
                    }}
                    className="btn btn-primary"
                  >
                    {t('quiz.allCategories')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sidebar */}
        <div className="quiz-sidebar">
          {/* Streak Card */}
          <div className="quiz-streak-card">
            <div className="quiz-streak-card__icon">
              <Icons.Flame size={28} />
            </div>
            <div className="quiz-streak-card__value">{progress.currentStreak}</div>
            <div className="quiz-streak-card__label">{t('quiz.streakCard.current')}</div>
            <div className="quiz-streak-card__best">
              {t('quiz.streakCard.best')} : <span>{progress.bestStreak}</span>
            </div>
          </div>

          {/* Progress by Category */}
          <div className="quiz-progress-card">
            <h3 className="quiz-progress-card__title">
              <Icons.PieChart size={16} />
              {t('quiz.progress')}
            </h3>
            <div className="quiz-progress-card__list">
              {categoriesWithCounts.slice(0, 6).map((cat) => {
                const stats = progress.categoryStats[cat.key as QuizCategory];
                const categoryAccuracy = stats.total > 0
                  ? Math.round((stats.correct / stats.total) * 100)
                  : 0;

                return (
                  <div key={cat.key} className="quiz-progress-item">
                    <div
                      className="quiz-progress-item__dot"
                      style={{ background: cat.color }}
                    />
                    <div className="quiz-progress-item__info">
                      <div className="quiz-progress-item__name">{cat.label}</div>
                      <div className="quiz-progress-item__bar">
                        <div
                          className="quiz-progress-item__fill"
                          style={{
                            width: `${(stats.total / cat.total) * 100}%`,
                            background: cat.color
                          }}
                        />
                      </div>
                    </div>
                    <div className="quiz-progress-item__score">
                      {stats.total > 0 ? `${categoryAccuracy}%` : '-'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reset Button */}
          {progress.totalAnswered > 0 && (
            <button
              onClick={handleReset}
              className="btn btn-ghost"
              style={{
                width: '100%',
                fontSize: '13px',
                color: 'var(--muted)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Icons.RefreshCw size={14} />
              {t('quiz.restart')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
